"use client";

import { useEffect, useState } from "react";
import { useSession } from "@/lib/auth-react";
import { authFetch } from "@/utils/auth-fetch";

export interface PaymentAccess {
  /** May this user pay right now (globally enabled OR an allowlisted test user)? */
  enabled: boolean;
  isTestUser: boolean;
  /** Forced flat price (USD) for a test user, else null. */
  priceOverride: number | null;
}

const denied: PaymentAccess = { enabled: false, isTestUser: false, priceOverride: null };

/**
 * Per-user payment access for gating buy/recharge CTAs. Global `payments.enabled`
 * (from useSiteStatus) hides these when payments are off — but allowlisted test
 * users must still see them, which this resolves. Signed-in access stays closed
 * until verified and refreshes on focus and every 30 seconds.
 * Server-side checkout rechecks settings on every request.
 */
export function usePaymentAccess(): PaymentAccess | null {
  const { data: session, status } = useSession();
  const userId = session?.user?.id;
  const token = (session as unknown as { accessToken?: string })?.accessToken || "";
  const [result, setResult] = useState<{ userId: string; token: string; access: PaymentAccess } | null>(null);

  useEffect(() => {
    if (!userId) return;
    let active = true;
    let loading = false;
    const refresh = async () => {
      if (loading) return;
      loading = true;
      let access = denied;
      try {
        const response = await authFetch("/api/payments/context", token);
        if (response.ok) {
          const body = await response.json();
          if (body?.data?.enabled === true) access = body.data;
        }
      } catch { /* Unknown access stays closed. */ }
      finally { loading = false; }
      if (active) setResult({ userId, token, access });
    };
    void refresh();
    const interval = window.setInterval(() => void refresh(), 30_000);
    window.addEventListener("focus", refresh);
    return () => {
      active = false;
      window.clearInterval(interval);
      window.removeEventListener("focus", refresh);
    };
  }, [userId, token]);

  // Anonymous package browsing uses the public flag; checkout still requires login.
  if (status === "unauthenticated") return null;
  return result?.userId === userId && result?.token === token ? result.access : denied;
}
